import { Box, IconButton } from "@mui/material"
import { KeyboardArrowDownOutlined, KeyboardArrowUpOutlined } from "@mui-symbols-material/w300"
import type { ColumnDef } from "@tanstack/react-table"
import { useMemo } from "react"

import {
  CostBreakdownActions,
  CostBreakdownCompletedCell,
  CostBreakdownCostSourceCell,
} from "../components/CostBreakdownTable"
import { COST_BREAKDOWN_DECIMAL_PRECISION } from "../constants"

import { PermissionActionType } from "@/constants/permissions"
import type { GoodsReceipt, GoodsReceiptStatus } from "@/graphql/codegen/graphql"
import { usePermissionManager } from "@/hooks/usePermissionManager"
import { useGoodsReceiptColumns } from "@/screens/WarehouseReceiving/hooks"
import { formatNumber } from "@/utils/number"

interface UseCostBreakdownColumnsProps {
  status: GoodsReceiptStatus
  onEdit: (goodsReceipt: GoodsReceipt) => void
  onCostSourceChange: (goodsReceipt: GoodsReceipt) => void
  onMarkAsNotReceived?: (goodsReceipt: GoodsReceipt) => void
}

export const useCostBreakdownColumns = ({
  status,
  onEdit,
  onCostSourceChange,
  onMarkAsNotReceived,
}: UseCostBreakdownColumnsProps): ColumnDef<GoodsReceipt>[] => {
  const { hasPermissions } = usePermissionManager()
  const canMaintain = hasPermissions([PermissionActionType.COST_BREAKDOWN_MAINTENANCE])

  const warehouseColumns = useGoodsReceiptColumns({
    status,
    onEdit: undefined,
    onMarkAsNotReceived,
  })

  const sharedGrCostColumn: ColumnDef<GoodsReceipt> = useMemo(
    () => ({
      id: "sharedGrCost",
      accessorKey: "sharedGrCost",
      header: "Shared GR Cost",
      enableSorting: true,
      size: 150,
      cell: ({ row }) => {
        const sharedGrCost = row.original.costBreakdown?.sharedGrCost
        return sharedGrCost
          ? `$${formatNumber(sharedGrCost, COST_BREAKDOWN_DECIMAL_PRECISION, COST_BREAKDOWN_DECIMAL_PRECISION)}`
          : "-"
      },
    }),
    []
  )

  const completionStatusColumn: ColumnDef<GoodsReceipt> = useMemo(
    () => ({
      id: "completionStatus",
      accessorKey: "completionStatus",
      header: "Product Cost Completed",
      enableSorting: true,
      size: 200,
      cell: ({ row }) => (
        <CostBreakdownCompletedCell
          completed={row.original.costBreakdown?.lineCostBreakdowns?.every((breakdown) => breakdown.isCompleted)}
        />
      ),
    }),
    []
  )

  const costSourceColumn: ColumnDef<GoodsReceipt> = useMemo(
    () => ({
      id: "costSource",
      header: "Cost Source",
      enableSorting: false,
      size: 112,
      cell: ({ row }) => <CostBreakdownCostSourceCell isManagedInAs400={row.original.isCostManagedInAs400} />,
    }),
    []
  )

  const costBreakdownActionsColumn: ColumnDef<GoodsReceipt> = useMemo(
    () => ({
      accessorKey: "actions",
      header: "",
      enableSorting: false,
      size: 64,
      cell: ({ row }) => {
        return (
          <Box className='flex items-center justify-end gap-x-3' onClick={(e) => e.stopPropagation()}>
            {canMaintain && (
              <CostBreakdownActions
                goodsReceipt={row.original}
                onEdit={onEdit}
                onCostSourceChange={onCostSourceChange}
              />
            )}
            <IconButton size='small' onClick={row.getToggleExpandedHandler()}>
              {row.getIsExpanded() ? <KeyboardArrowUpOutlined /> : <KeyboardArrowDownOutlined />}
            </IconButton>
          </Box>
        )
      },
    }),
    [onEdit, canMaintain, onCostSourceChange]
  )

  return useMemo(() => {
    if (warehouseColumns.length === 0)
      return [sharedGrCostColumn, completionStatusColumn, costSourceColumn, costBreakdownActionsColumn]

    const result: ColumnDef<GoodsReceipt>[] = []
    warehouseColumns.forEach((column) => {
      if ((column as any).accessorKey === "actions") {
        result.push(sharedGrCostColumn, completionStatusColumn, costSourceColumn, costBreakdownActionsColumn)
      } else {
        result.push(column)
      }
    })
    return result
  }, [warehouseColumns, sharedGrCostColumn, completionStatusColumn, costSourceColumn, costBreakdownActionsColumn])
}
